import classNames from 'classnames'
import React, { CSSProperties } from 'react'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body1, Body2, Caption1, Caption2, Title1, Title2 } from '@ui/Typography/Typography'
import { Pimple, PimpleProps } from '@ui/components/Pimple'
import {
  NeutralInverseWhiteColorType,
  OutlineColorType,
  SurfaceColorType,
  TextColorType,
} from '@ui/design-system/colors/colors'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import { Box } from '@shared/components/Box'

import { SquircleOutline, SquircleShape } from './assets/Squircle'

import styles from './RichIcon.module.scss'

type Size = 'x16' | 'x20' | 'x24' | 'x32' | 'x40' | 'x48' | 'x56' | 'x64'

export type RichIconProps = {
  icon?: IconRendererType
  size?: Size
  label?: string
  labelColor?: TextColorType
  border?: OutlineColorType
  pimple?: PimpleProps
  bgImage?: string | null
  bgImageFit?: 'cover' | 'contain'
} & (
  | { shape: 'circle' | 'squircle'; bg?: SurfaceColorType | NeutralInverseWhiteColorType }
  | { shape?: never; bg?: never }
)

export const RichIcon: React.FC<RichIconProps> = (props) => {
  const {
    icon,
    border,
    label,
    labelColor,
    size = 'x20',
    bg,
    shape,
    pimple,
    bgImage,
    bgImageFit = 'cover',
  } = props

  const isShape = !!shape
  const isCircle = shape === 'circle'
  const isSquircle = shape === 'squircle'

  const adjustedSize = isShape ? iconSizeShapeDict[size] : iconSizeDict[size]
  const LabelElement = labelSizeDict[size]
  const pimpleSize = pimpleSizeDict[size]

  const Label = () =>
    label ? (
      <Box px={'x4'}>
        <LabelElement color={labelColor}>
          <Truncate>{label}</Truncate>
        </LabelElement>
      </Box>
    ) : null

  const sizeStyle: CSSProperties = {
    width: iconSizeDict[size],
    height: iconSizeDict[size],
    minWidth: iconSizeDict[size],
    maxWidth: iconSizeDict[size],
    minHeight: iconSizeDict[size],
    maxHeight: iconSizeDict[size],
    ...(bgImage && {
      backgroundImage: `url("${bgImage}")`,
      backgroundSize: bgImageFit === 'contain' ? `${iconSizeShapeDict[size]}px` : 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: bgImageFit === 'contain' ? 'no-repeat' : undefined,
    }),
  }

  if (isSquircle) {
    return (
      <div
        style={sizeStyle}
        className={classNames(styles.richIcon, styles.squircle, styles[`size-${size}`])}
      >
        <div style={sizeStyle} className={styles.squircleShape}>
          {bg && <SquircleShape color={bg} size={iconSizeDict[size]} bgImage={bgImage} />}
        </div>
        <div className={styles.elementWrapper}>
          <SquircleOutline size={iconSizeDict[size]} color={border} />
        </div>
        <div className={styles.elementWrapper}>
          <Label />
          {icon && <IconRenderer icon={icon} size={adjustedSize} />}
        </div>
        {pimple && (
          <div
            className={classNames(styles.pimple, {
              [styles.pimpleStrokeOutlined]: !!pimple.bg,
            })}
          >
            <Pimple {...pimple} size={pimpleSize} />
          </div>
        )}
      </div>
    )
  }

  return (
    <Box
      bg={isCircle ? bg : undefined}
      radius={isCircle ? 'round' : undefined}
      border={isShape ? border : undefined}
      flex
      align='center'
      justify='center'
      style={sizeStyle}
      className={classNames(styles.richIcon, styles[`size-${size}`])}
    >
      <Label />
      {icon && <IconRenderer icon={icon} size={adjustedSize} />}
      {pimple && (
        <div
          className={classNames(styles.pimple, {
            [styles.pimpleStrokeOutlined]: !!pimple.bg,
          })}
        >
          <Pimple {...pimple} size={pimpleSize} />
        </div>
      )}
    </Box>
  )
}

const iconSizeDict: Record<Size, number> = {
  x16: 16,
  x20: 20,
  x24: 24,
  x32: 32,
  x40: 40,
  x48: 48,
  x56: 56,
  x64: 64,
}

const iconSizeShapeDict: Record<Size, number> = {
  x16: 12,
  x20: 14,
  x24: 16,
  x32: 20,
  x40: 24,
  x48: 28,
  x56: 32,
  x64: 40,
}

const labelSizeDict: Record<Size, any> = {
  x16: Caption2,
  x20: Caption2,
  x24: Caption1,
  x32: Body2,
  x40: Body1,
  x48: Title2,
  x56: Title1,
  x64: Title1,
}

const pimpleSizeDict: Record<Size, any> = {
  x16: 'x6',
  x20: 'x6',
  x24: 'x6',
  x32: 'x16',
  x40: 'x16',
  x48: 'x16',
  x56: 'x24',
  x64: 'x24',
}
